import {useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  selectProposalInfo,
  selectRecurlyAccountInfo,
  setProposalInfo,
  setSelectedOptionId,
  setRecurlyAccountInfo,
  selectSelectedOptionId,
} from '../app-store/ProposalSlice'
import {validateEmailWithApi} from '../accountManagement/features/EmailLookup/EmailLookup.utils'
import {MC_2_0_SIGNATUREPLANS} from './constants'
import {
  APPOINTMENT_REQUIRED_CODES,
  APPOINTMENT_EXEMPT_CENTER_CODES,
  APPOINTMENT_REQUIRED_ENTITLEMENT_CODES,
  hasStarterOnboardingOptIn,
} from './scheduling-call-rules'

/**
 * Ensures proposalInfo is always populated in Redux.
 * If the store was reset (e.g. page reload), fetches from the API and
 * re-hydrates the slice before returning. Also restores the selectedOptionId
 * from the option marked isSelected in the fetched data.
 *
 * @param {object} params
 * @param {string} params.proposalId - the proposal UUID from the URL param
 * @param {Function} params.client   - the proposal-scoped auth client
 * @param {boolean} [params.enabled] - set false to defer fetching until a
 *   prerequisite (e.g. the proposal-auth token) is ready
 * @returns {{ proposalInfo: object, loading: boolean, error: string | null }}
 */
export function useEnsureProposalInfo({proposalId, client, enabled = true}) {
  const dispatch = useDispatch()
  const proposalInfo = useSelector(selectProposalInfo)
  const [loading, setLoading] = useState(!proposalInfo?.proposalId)
  const [error, setError] = useState(null)
  const fetchedFor = useRef(null)
  const selectedOptionId = useSelector(selectSelectedOptionId)

  useEffect(() => {
    if (!proposalId || !enabled) return
    // Data already in store and we've not changed proposals — nothing to do
    if (proposalInfo?.proposalId && fetchedFor.current === proposalId) return
    // Data already in store for this proposalId (e.g. navigated from proposal view)
    if (proposalInfo?.proposalId === proposalId) {
      fetchedFor.current = proposalId
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    fetchedFor.current = proposalId

    client(`proposals/${proposalId}`)
      .then(data => {
        dispatch(setProposalInfo(data))
        // Restore the previously selected option from the fetched data
        const selectedOption = (data.options ?? []).find(
          item => item.isSelected,
        )
        if (selectedOption) {
          dispatch(setSelectedOptionId(selectedOption.optionId ?? null))
        }

        setLoading(false)
      })
      .catch(err => {
        setError(err.message || 'Failed to load proposal')
        setLoading(false)
      })
  }, [proposalId, proposalInfo?.proposalId, enabled])

  return {proposalInfo, loading, error, selectedOptionId}
}

/**
 * Ensures recurlyAccountInfo is always populated in Redux.
 * If the store was reset (e.g. page reload), calls validateEmailWithApi using
 * proposalInfo.clientEmail to re-hydrate the recurly account slice.
 *
 * @param {object} params
 * @param {object} params.proposalInfo - proposal info object (must contain clientEmail)
 * @param {Function} params.client     - the proposal-scoped auth client
 * @returns {{ recurlyAccountInfo: object, loading: boolean, error: string | null }}
 */
export function useEnsureRecurlyAccountInfo({proposalInfo, client}) {
  const dispatch = useDispatch()
  const recurlyAccountInfo = useSelector(selectRecurlyAccountInfo)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const fetchedFor = useRef(null)

  useEffect(() => {
    const email = proposalInfo?.clientEmail
    if (!email) return
    // Already populated — mark as handled and skip fetch
    if (Object.keys(recurlyAccountInfo).length > 0) {
      fetchedFor.current = email
      return
    }
    // Already fetched for this email in this session
    if (fetchedFor.current === email) return

    setLoading(true)
    setError(null)
    fetchedFor.current = email

    validateEmailWithApi({email, client, flowType: 'Proposal'})
      .then(({meta}) => {
        if (meta?.subDetails) {
          dispatch(setRecurlyAccountInfo(meta.subDetails))
        }
        setLoading(false)
      })
      .catch(err => {
        setError(err.message || 'Failed to fetch recurly account info')
        setLoading(false)
      })
  }, [proposalInfo?.clientEmail])

  return {recurlyAccountInfo, loading, error}
}

// ---------------------------------------------------------------------------
// Schedule-call detection
// Rule lives in utils/scheduling-call-rules.js (shared with the Account
// Setup / Purchase Link flow's per-location gate so both flows agree).
// Source of truth: docs/onboarding-scheduled-calls.md
// ---------------------------------------------------------------------------

/**
 * Returns true if the selected proposal option requires a scheduling call.
 * See itemsRequireScheduleCall for the evaluation order.
 *
 * @param {object} proposalInfo - proposal info object containing options
 * @returns {boolean}
 */
export function isScheduleCallRequired(proposalInfo) {
  const selectedOption = (proposalInfo?.options ?? []).find(
    opt => opt.isSelected,
  )
  if (!selectedOption) return false

  const items = selectedOption.items ?? []

  // Thryv Starter + Growth Package with includeOnboarding=Yes → kickoff required
  if (hasStarterOnboardingOptIn(items)) return true

  return items.some(item => {
    const isNew = !item.alreadyIncluded
    const cfg = item.configuration ?? item.configuredOptions ?? {}
    const onboardingDeclined = cfg.onboarding === 'no'
    const entitlements = item.entitlements ?? []

    // (1) MC Signature conditional kickoff ——————————————————————————————
    // Must run before the generic CENTER check so it can short-circuit.
    // Kickoff required when onboarding was NOT declined, OR when the
    // 'ent-5-website' entitlement is bundled in (overrides declined flag).
    // Note: in the proposal data structure, entitlements selected via
    // ProductPlanSelector are stored in item.addons (not item.entitlements),
    // and after normalization via toApiAddon their code is in productCode.
    if (isNew && MC_2_0_SIGNATUREPLANS.includes(item.productCode)) {
      const addons = item.addons ?? []
      const hasWebsiteEnt =
        entitlements.some(ent => ent.code === 'ent-5-website') ||
        addons.some(addon => addon.productCode === 'ent-5-website')
      return !onboardingDeclined || hasWebsiteEnt
    }

    // (2) Generic CENTER check ————————————————————————————————————————
    // Any new, non-exempt, non-declined center requires a kickoff call.
    if (
      isNew &&
      item.productType === 'CENTER' &&
      !onboardingDeclined &&
      !APPOINTMENT_EXEMPT_CENTER_CODES.has(item.productCode)
    )
      return true

    // (3) Entitlement check ——————————————————————————————————————————
    // Catches upgrades where a new kickoff entitlement (ent-5-website,
    // ent-5-custvideo, ent-5-socialcontentgen) is added to a center the
    // account already holds. item.alreadyIncluded may be true here.
    // Guard against exempt centers inadvertently matching.
    const hasNewKickoffEntitlement =
      !APPOINTMENT_EXEMPT_CENTER_CODES.has(item.productCode) &&
      entitlements.some(
        ent =>
          !ent.alreadyIncluded &&
          APPOINTMENT_REQUIRED_ENTITLEMENT_CODES.has(ent.code),
      )
    if (hasNewKickoffEntitlement) return true

    // (4) Product code check — apps, add-ons, one-time items ————————
    if (isNew && APPOINTMENT_REQUIRED_CODES.has(item.productCode)) return true

    // (5) Nested addon check ——————————————————————————————————————————
    return (item.addons ?? []).some(
      addon =>
        !addon.alreadyIncluded &&
        APPOINTMENT_REQUIRED_CODES.has(addon.productCode),
    )
  })
}
